import { Vue } from 'vue-class-component';

import ProductsUtils from './ProductsUtils';
import { process, DataResult, State, SortDescriptor } from '@progress/kendo-data-query';
import { FamilyOptionModel, FilterOptionValueModel, FilterOptionSelectedValueModel, IFamilyOptionModel } from '../view-models/FamilyOptionModel';
import { Component, Prop, Watch } from 'vue-property-decorator';
import _, { filter, forEach } from 'lodash';
import { GridPageChangeEvent, GridSortChangeEvent, GridDataStateChangeEvent } from '@progress/kendo-vue-grid';

import { resources } from '../resources';
import { GridSortSettings } from '@progress/kendo-vue-grid/dist/npm/interfaces/GridSortSettings';

export default class ProductsList extends Vue {
    @Prop(Object)
    public searchResponse: any | null;

    @Prop(String)
    public languageId: string = 'it-IT';

    @Prop(String)
    public searchTerm: string | null = null;

    @Prop(String)
    public gtagActionField: string | null = null;

    @Watch('searchResponse')
    searchResponseModified() {
        var instance = this;

        if (instance.currentSearchId != instance.searchResponse.searchId) {
            return;
        }

        instance.total = instance.searchResponse.results.totalCount as number;

        if (!instance.familyOptions && instance.searchResponse.familyOptions &&
            Object.keys(instance.searchResponse.familyOptions).length > 0) {
            instance.familyOptions = instance.searchResponse.familyOptions;
        }

        if (!instance.listColumns) {
            instance.apiColumns = instance.searchResponse.columns as any[];

            instance.bindColumns();
        }

        var resFilters = instance.searchResponse.filters as FamilyOptionModel[];

        if (!instance.filterOptions || instance.filterOptions.length == 0) {
            // initialize filters and selected values
            instance.filterOptions = resFilters;

            instance.initializeSelectedValues();
        }
        else {
            // update filters facets and values
            for (var i = 0; i < instance.filterOptions.length; i++) {
                var instanceFilter = instance.filterOptions[i];
                var resFilter = _.find(resFilters, (f) => f.option?.code == instanceFilter.option?.code);

                if (instanceFilter.option && instanceFilter.option.facets) {
                    instanceFilter.option.facets.splice(0, instanceFilter.option.facets.length);
                    instanceFilter.option.values.splice(0, instanceFilter.option.values.length);

                    instanceFilter.option.facets.push.apply(instanceFilter.option.facets, resFilter?.option?.facets || []);
                    instanceFilter.option.values.push.apply(instanceFilter.option.values, resFilter?.option?.values || []);
                }
            }
        }

        if (_.filter(instance.searchResponse.results.items, (p) => p.masterName).length > 0) {
            instance.group = this._defaultGroup;
        }
        else {
            instance.group = [];
        }

        if (!instance.products) {
            instance.products = [];
        }

        if (instance.products.length > 0) {
            instance.products.splice(0, instance.products.length);
        }

        //instance.products.push.apply(instance.products, ProductsUtils.flatMapProducts(instance.searchResponse.results.items));
        instance.products.push.apply(instance.products, instance.searchResponse.results.items);

        instance.bindData();

        instance.searchIsLoading = false;

        // to apply widths with visible grid
        setTimeout(instance.bindColumns, 1);
    }

    public productsData: DataResult | null = null;

    public familyOptions: any | null = null;

    public filterOptions: FamilyOptionModel[] = [];

    public selectedFilterValues: {
        [name: string]: FilterOptionSelectedValueModel
    } | null = null;

    public listColumns: any[] | null = null;

    public sort: SortDescriptor[] = [
        { field: '#PRODUCT_NAME#', dir: 'asc' }
    ];

    public sortable: GridSortSettings = { allowUnsort: false };

    public group: any[] = [];

    private _defaultGroup: any[] = [{ field: 'masterName' }];

    public skip: number = 0;
    public take: number = 20;
    public total: number = 0;

    public resources = resources;

    public searchIsLoading: boolean = true;

    private prevSearchTimeout: number | null = null;
    private currentSearchId: number | null = null;

    private products: any[] | null = null;

    private apiColumns: any[] | null = null;
    private columnsMinWidth: number = 70;
    private skuColumnWidth: number = 190;

    private filtersQsSeparator: string = '|';

    get availabliltyFilterOption(): IFamilyOptionModel {
        return _.find(this.filterOptions, (f) => f.option?.code == 'isavailable') || new FamilyOptionModel();
    }

    get designationFilterOption(): IFamilyOptionModel {
        return _.find(this.filterOptions, (f) => f.option?.code.toLocaleLowerCase() == 'q') || new FamilyOptionModel();
    }

    get familyFilterOption(): IFamilyOptionModel | null {
        return _.find(this.filterOptions, (f) => f.option?.code == 'FamilyId') || null;
    }

    get isTouchDevice(): boolean {
        return (('ontouchstart' in window) || (navigator.maxTouchPoints > 0) || (navigator.msMaxTouchPoints > 0))
            && window.innerWidth < 1025;
    }

    onFilterChanged() {
        var instance = this;

        instance.skip = 0;

        instance.currentSearchId = Math.random();

        instance.pushSearchHistory();

        instance.fetchData(instance.currentSearchId);
    }

    onInputFilter() {
        var instance = this;

        if (instance.prevSearchTimeout) {
            window.clearTimeout(instance.prevSearchTimeout);
        }

        instance.prevSearchTimeout = window.setTimeout(instance.onFilterChanged, 300);
    }

    renderCell(props: any) {
        let result = props.field !== undefined && props.rowType !== "groupHeader" && props.rowType != "groupFooter";

        return result;
    }

    getOptionValueCountFacet(optionValue: FilterOptionValueModel, option: FamilyOptionModel): number {
        if (!option || !option.option || !option.option?.facets)
            return 0;

        var facet = _.find(option.option.facets, (f) => f.code == option.option?.code);

        if (facet)
            return facet.values[optionValue.code] ?? 0;

        return 0;
    }

    hasOptionSelectedValue(optionCode: string): boolean {
        if (!this.selectedFilterValues)
            return false;

        var selValue = this.selectedFilterValues[optionCode];

        if (selValue) {
            return selValue.hasValue();
        }

        return false;
    }

    get selectedFiltersNamesAndValueLabels() {
        var instance = this;
        if (!instance.selectedFilterValues)
            return {} as {
                [name: string]: FilterOptionSelectedValueModel
            };

        var out = [];

        for (var selFilterOptionCode in instance.selectedFilterValues) {
            var selValue = instance.selectedFilterValues[selFilterOptionCode];

            if (selValue.hasValue()) {
                var filterOption = _.find(instance.filterOptions, (o) => o.option?.code == selFilterOptionCode);
                var filterName = filterOption?.option?.name;
                var filterValueLabel = '';

                if (filterOption?.option?.type == "Enumeration") {
                    var selValueNames = _.map(_.filter(filterOption.option.values, (v) => selValue.codes.indexOf(v.code) >= 0),
                        (o) => o.name);

                    filterValueLabel = selValueNames.join(", ");
                }
                else {
                    filterValueLabel = instance.getSelectedValueLabel(selValue);
                }

                out.push({ optionCode: selFilterOptionCode, name: filterName || selFilterOptionCode, value: filterValueLabel });
            }
        }

        return out;
    }

    public getProductAttributeName(optionCode: string) {
        var instance = this;

        if (!instance.familyOptions || Object.keys(instance.familyOptions).length == 0)
            return '';

        return instance.familyOptions[optionCode] || '';
    }

    get mainFilterOptions(): FamilyOptionModel[] {
        if (!this.filterOptions)
            return [];

        return _.filter(this.filterOptions, (o) => o.optionGroup == 'Main');
    }

    get advancedFilterOptions(): FamilyOptionModel[] {
        if (!this.filterOptions)
            return [];

        return _.filter(this.filterOptions, (o) => o.optionGroup == 'Advanced');
    }

    // these are for server side paging/sorting
    onPageChanged(e: GridPageChangeEvent) {
        this.skip = e.page.skip;

        this.bindData();

        this.pushSearchHistory();
    };

    onSortChanged(e: GridSortChangeEvent) {
        this.sort = e.sort;

        this.products = ProductsUtils.orderBy(this.products, this.sort);

        this.bindData();

        this.pushSearchHistory();
    }

    onRemoveFilter(optionCode: string) {
        if (!this.selectedFilterValues)
            return;

        this.selectedFilterValues[optionCode].clearValue();

        this.onFilterChanged();
    }

    onResetFilters(e: Event) {
        e.preventDefault();

        var instance = this;

        if (!instance.filterOptions)
            return;

        instance.resetSelectedValues();

        instance.onFilterChanged();
    }

    getSelectedValueLabel(selValue: FilterOptionSelectedValueModel): string {
        if (selValue.codes.length > 0) {
            return selValue.codes.join(", ");
        }

        if (selValue.minValue && selValue.maxValue) {
            return (this.resources['FromToNumber'] as string).format(selValue.minValue.toLocaleString(), selValue.maxValue.toLocaleString());
        }

        if (selValue.minValue) {
            return (this.resources['FromNumber'] as string).format(selValue.minValue.toLocaleString());
        }

        if (selValue.maxValue) {
            return (this.resources['ToNumber'] as string).format(selValue.maxValue.toLocaleString());
        }

        if (selValue.text) {
            return selValue.text;
        }

        return '';
    }

    localizeCellValue(value: any): any {
        if (typeof value === "number") {
            return value.toLocaleString(this.languageId, { useGrouping: false });
        }
        return value;
    }

    private initializeSelectedValues(): void {
        // initialize filters
        var instance = this;

        if (!instance.selectedFilterValues)
            instance.selectedFilterValues = {};

        for (var i = 0; i < instance.filterOptions.length; i++) {
            let filter = instance.filterOptions[i];

            if (filter.option && !instance.selectedFilterValues[filter.option.code]) {
                instance.selectedFilterValues[filter.option.code] = filter.defaultValue ?
                    Object.assign(new FilterOptionSelectedValueModel(), filter.defaultValue) :
                    new FilterOptionSelectedValueModel();

                if (!instance.selectedFilterValues[filter.option.code].codes)
                    instance.selectedFilterValues[filter.option.code].codes = [];
            }
        }
    }

    private resetSelectedValues(): void {
        // initialize filters
        var instance = this;

        instance.selectedFilterValues = {};

        for (var i = 0; i < instance.filterOptions.length; i++) {
            let filter = instance.filterOptions[i];

            if (filter.option)
                instance.selectedFilterValues[filter.option.code] = new FilterOptionSelectedValueModel();
        }
    }

    private getFieldSortType(optionCode: string) {
        var option = _.find(this.filterOptions, (o) => o.option?.code == optionCode);

        switch (option?.option?.type) {
            case 'NumberInt':
            case 'NumberDecimal':
                return 'Number';
            default:
                return 'String';
        }
    }

    private getApiOrderBy(): any[] {
        var instance = this;

        return _.map(instance.sort, (s) => {
            return {
                field: s.field,
                direction: s.dir?.toUpperCase() || 'asc',
                sortType: instance.getFieldSortType(s.field)
            }
        });
    }

    private getPageIndex(): number {
        return (this.take > 0 ? this.skip / this.take : 0);
    }

    private pushSearchHistory(): void {
        var instance = this;

        //var stateObj: any = {
        //    pageIndex: instance.getPageIndex(),
        //    sort: [],
        //    filters: {}
        //};

        // build query string
        var page: number = instance.getPageIndex() + 1;

        // page
        var querystring = `?page=${page}`;

        // order by
        if (instance.sort.length > 0) {
            var orderBySerialized = ProductsUtils.getOrderByQueryStringParam(instance.sort);
            querystring += `&orderby=${orderBySerialized}`;

            //stateObj.sort = _.map(instance.sort, (s) => { return { field: s.field, dir: s.dir || 'asc' } });
        }

        //// search term
        //if (instance.searchTerm) {
        //    querystring += `&q=${instance.searchTerm}`;
        //}

        var filterParams: string[] = [];

        if (instance.selectedFilterValues)
            Object.keys(instance.selectedFilterValues).forEach(optionCode => {
                var filter = instance.selectedFilterValues[optionCode];

                let escapedOptionCode = optionCode.replaceAll('#', '@');

                if (filter.codes.length > 0) {
                    filterParams.push(`${escapedOptionCode.toLowerCase()}__codes=${filter.codes.join(',')}`);
                }

                if (filter.minValue) {
                    filterParams.push(`${escapedOptionCode.toLowerCase()}__min=${filter.minValue}`);
                }

                if (filter.maxValue) {
                    filterParams.push(`${escapedOptionCode.toLowerCase()}__max=${filter.maxValue}`);
                }

                if (filter.text && filter.text != '') {
                    filterParams.push(`${escapedOptionCode.toLowerCase()}__text=${filter.text.replaceAll(instance.filtersQsSeparator, '')}`);
                }

                if (optionCode == 'isavailable') {
                    filterParams.push(`${escapedOptionCode.toLowerCase()}=${filter.boolValue ? '1' : '0'}`);
                }
            });

        if (filterParams.length > 0) {
            querystring += `&filters=${filterParams.join(instance.filtersQsSeparator)}`;
        }

        var pathAndQueryString = location.pathname + querystring;

        history.pushState(null, '', pathAndQueryString);
        instance.$emit('onHistoryPushed', pathAndQueryString);
    }

    private rebuildFiltersFromHistoryState() {
        var instance = this;

        if (location.search && location.search != '') {
            var urlParams = new URLSearchParams(location.search);

            instance.selectedFilterValues = {};

            urlParams.forEach((value: string, key: string) => {
                if (!value || value == '')
                    return;

                switch (key.toLowerCase()) {
                    case "page":
                        var pageNumber = Number(value);

                        if (pageNumber != NaN) {
                            instance.skip = (pageNumber - 1) * instance.take;
                        }

                        break;

                    case "orderby":
                        var sortByQueryString = ProductsUtils.getSortByOrderByQueryStringParam(value);

                        instance.sort.splice(0, instance.sort.length);
                        instance.sort.push.apply(instance.sort, sortByQueryString);

                        break;

                    case "filters":
                        if (!instance.selectedFilterValues) {
                            instance.selectedFilterValues = {};
                        }

                        var allFilters = value.split(instance.filtersQsSeparator)

                        for (var i = 0; i < allFilters.length; i++) {

                            var filterParts = allFilters[i].split('=');

                            var filterOptionCode = filterParts[0].toLowerCase().replaceAll('@', '#');
                            var filterValue = filterParts[1];

                            if (filterOptionCode.endsWith('__min')) {
                                var minValue = Number(filterValue);

                                if (minValue && minValue != NaN) {
                                    filterOptionCode = filterOptionCode.substring(0, filterOptionCode.length - '__min'.length);

                                    if (!instance.selectedFilterValues[filterOptionCode])
                                        instance.selectedFilterValues[filterOptionCode] = new FilterOptionSelectedValueModel();

                                    instance.selectedFilterValues[filterOptionCode].minValue = minValue;
                                }
                            }
                            else if (filterOptionCode.endsWith('__max')) {
                                var maxValue = Number(filterValue);

                                if (maxValue && maxValue != NaN) {
                                    filterOptionCode = filterOptionCode.substring(0, filterOptionCode.length - '__max'.length);

                                    if (!instance.selectedFilterValues[filterOptionCode])
                                        instance.selectedFilterValues[filterOptionCode] = new FilterOptionSelectedValueModel();

                                    instance.selectedFilterValues[filterOptionCode].maxValue = maxValue;
                                }
                            }
                            else if (filterOptionCode.endsWith('__codes')) {
                                filterOptionCode = filterOptionCode.substring(0, filterOptionCode.length - '__codes'.length);

                                if (filterOptionCode == 'familyid')
                                    filterOptionCode = 'FamilyId';

                                instance.selectedFilterValues[filterOptionCode] = new FilterOptionSelectedValueModel();
                                instance.selectedFilterValues[filterOptionCode].codes = filterValue.split(',');

                            }
                            else if (filterOptionCode.endsWith('__text')) {
                                filterOptionCode = filterOptionCode.substring(0, filterOptionCode.length - '__text'.length);

                                if (filterOptionCode == 'sku')
                                    filterOptionCode = 'Sku';
                                else if (filterOptionCode == '#product_name#')
                                    filterOptionCode = '#PRODUCT_NAME';

                                instance.selectedFilterValues[filterOptionCode] = new FilterOptionSelectedValueModel();
                                instance.selectedFilterValues[filterOptionCode].text = filterValue;
                            }
                            else if (filterValue == "1" || filterValue == "0") {
                                instance.selectedFilterValues[filterOptionCode] = new FilterOptionSelectedValueModel();
                                instance.selectedFilterValues[filterOptionCode].boolValue = filterValue == "1";
                            }
                        }

                        break;
                }
            });
        }
    }

    private bindColumns(): void {
        var instance = this;

        if (!instance.apiColumns)
            return;

        var grid: any = document.querySelector(".k-grid-content");

        var setMinWidth = false;
        var columnFullWidth = 0;
        var columnFullWidthRounded = 0;
        var columnsCount = 0;
        var gridCurrent = 0;
        var fixedPadding = instance.skuColumnWidth + 32;

        if (grid) {
            gridCurrent = grid.clientWidth;

            // group icon width (32) + fixed sku column
            var minGridWidth = fixedPadding;
            columnsCount = 1;

            var _recursivelyGetMinGridWidth = (column: any) => {
                if (column.field != '#PRODUCT_NAME#') {
                    if (column.field && column.field != "") {
                        columnsCount += 1;
                        minGridWidth += instance.columnsMinWidth;
                    }
                    else if (column.children && column.children.length > 0) {
                        for (var i = 0; i < column.children.length; i++) {
                            _recursivelyGetMinGridWidth(column.children[i]);
                        }
                    }
                }
            };

            instance.apiColumns.forEach(c => _recursivelyGetMinGridWidth(c));

            setMinWidth = (gridCurrent < minGridWidth);

            columnFullWidth = instance.columnsMinWidth + ((gridCurrent - minGridWidth) / columnsCount);
            columnFullWidthRounded = Math.round(columnFullWidth);
        }

        var buildColumnsCount = 0;
        var _iterateRecursivelyColumns = (column: any) => {
            if (column.field == '#PRODUCT_NAME#') {
                buildColumnsCount += 1;

                column.cell = 'designationTemplate';
                column.width = instance.skuColumnWidth;
                column.locked = true;
            }
            else if (!column.children || column.children.length == 0) {
                buildColumnsCount += 1;

                column.cell = "localizationTemplate";

                if (setMinWidth) {
                    column.width = instance.columnsMinWidth;
                }
                else if (columnFullWidthRounded > 0) {
                    column.width = (buildColumnsCount == columnsCount) ?
                        Math.floor(gridCurrent - fixedPadding - (columnFullWidth * (columnsCount - 1))) + columnFullWidthRounded :
                        columnFullWidthRounded;

                    if (buildColumnsCount == columnsCount) {
                        //console.log(column.width);
                    }
                }
            }
            else {
                column.children = _.map(column.children, (child) => _iterateRecursivelyColumns(child));
            }

            return column;
        };

        instance.listColumns = _.map(instance.apiColumns, (col) => _iterateRecursivelyColumns(col));
    }

    private fetchData(thisSearchId: number | null) {
        var instance = this;

        var pageIndex: number = instance.getPageIndex();
        var orderBy = instance.getApiOrderBy();

        // show loader only in first search
        if (!thisSearchId)
            instance.searchIsLoading = true;

        var pathAndQueryString = (location.pathname + location.search);

        instance.$emit('onFetchRequested', pageIndex, instance.selectedFilterValues, orderBy, thisSearchId, pathAndQueryString, !thisSearchId);
    }

    private bindData(): void {
        var instance = this;

        var thisProducts = ProductsUtils.page(instance.products || [], instance.skip, instance.take);

        var thisProductsKendo = ProductsUtils.toKendoGridList(thisProducts);

        const dataState = {};

        instance.productsData = process(thisProductsKendo, dataState);

        try {
            if (thisProducts.length > 0) {
                // send gtag event

                var productsForGTag: any[] = thisProducts.map((p, i) => instance.mapProductForGTag(p, i + 1));

                var dlEvent = {
                    'event': 'eec.impressionView',
                    'ecommerce': {
                        'currencyCode': 'EUR',
                        'impressions': productsForGTag
                    }
                };

                dataLayer.push(dlEvent);
            }

        } catch (e) {
            // do nothing
        }
    }

    onProductClick(product: any) {
        try {
            var instance = this;

            var productForGTag = instance.mapProductForGTag(product);

            var dlEvent = {
                'event': 'eec.impressionClick',
                'ecommerce': {
                    'actionField': { 'list': instance.gtagActionField || 'Category List' },
                    'products': [productForGTag]
                }
            };

            dataLayer.push(dlEvent);

        } catch (e) {
            // do nothing
        }
    }

    private mapProductForGTag(product: any, position: number | null = null) {
        var instance = this;

        if (position) {
            return {
                'id': product.id,
                'name': product.name,
                'brand': 'UmbraGroup',
                'category': product.familyCode,
                'price': product.netPrice,
                'position': position,
                'list': instance.gtagActionField
            };
        }

        return {
            'id': product.id,
            'name': product.name,
            'brand': 'UmbraGroup',
            'category': product.familyCode,
            'price': product.netPrice
        }
    }

    private handleResize() {
        this.bindColumns();
    }

    private setupColumnsResize() {
        var instance = this;

        window.addEventListener("resize", instance.handleResize);
    }

    created() {
        this.setupColumnsResize();

        this.rebuildFiltersFromHistoryState();

        this.fetchData(null);
    }
}